/**
 * Subscription Plans Page
 * Display pricing tiers and handle checkout
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';

interface PricingTier {
  name: string;
  displayName: string;
  description: string;
  priceMonthly: number;
  priceAnnual: number;
  features: string[];
  popular?: boolean;
  cta: string;
  ctaAction: 'checkout' | 'free' | 'contact';
}

const SubscriptionPlans: React.FC = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tiers: PricingTier[] = [
    {
      name: 'free',
      displayName: 'Starter',
      description: 'Perfect for getting started',
      priceMonthly: 0,
      priceAnnual: 0,
      features: [
        '3 projects',
        '10 calculations per month',
        '5 PDF exports per month',
        'Basic calculators',
        'Watermarked PDFs',
        'Email support',
      ],
      cta: 'Start Free',
      ctaAction: 'free',
    },
    {
      name: 'professional',
      displayName: 'Professional',
      description: 'For professional engineers',
      priceMonthly: 29,
      priceAnnual: 290,
      features: [
        '25 projects',
        'Unlimited calculations',
        'Unlimited PDF exports',
        'All calculators',
        'NFPA 72 references',
        'Compliance warnings',
        'Custom branding',
        'Light watermark (removable)',
        'Priority email support',
      ],
      popular: true,
      cta: 'Start Professional',
      ctaAction: 'checkout',
    },
    {
      name: 'business',
      displayName: 'Business',
      description: 'For teams and firms',
      priceMonthly: 99,
      priceAnnual: 990,
      features: [
        '100 projects',
        'Unlimited calculations',
        'Unlimited PDF exports',
        'All Professional features',
        'Team collaboration (10 members)',
        'API access',
        'No watermark',
        'Advanced reporting',
        'Phone & email support',
      ],
      cta: 'Start Business',
      ctaAction: 'checkout',
    },
    {
      name: 'enterprise',
      displayName: 'Enterprise',
      description: 'For large organizations',
      priceMonthly: 299,
      priceAnnual: 2990,
      features: [
        'Unlimited projects',
        'Unlimited everything',
        'All Business features',
        'Unlimited team members',
        'White-label option',
        'Custom integrations',
        'Dedicated support',
        'SLA guarantee',
        'Custom contract',
      ],
      cta: 'Contact Sales',
      ctaAction: 'contact',
    },
  ];

  const handleSelectPlan = async (tier: PricingTier) => {
    setError(null);

    // Free tier - confirm without payment
    if (tier.ctaAction === 'free') {
      setLoading(true);
      try {
        const response = await fetch('/api/v1/payments/confirm-free-tier', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();

        if (response.ok) {
          navigate('/dashboard');
        } else {
          setError(data.error || 'Failed to confirm free tier');
        }
      } catch (err: any) {
        setError('Failed to confirm free tier');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Enterprise tier - contact sales
    if (tier.ctaAction === 'contact') {
      navigate('/contact-sales');
      return;
    }

    // Paid tiers - create checkout session
    setLoading(true);
    try {
      const response = await fetch('/api/v1/payments/create-checkout-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tier: tier.name,
          billingCycle,
        }),
      });

      const data = await response.json();

      if (response.ok && data.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.data.url;
      } else {
        setError(data.error || 'Failed to create checkout session');
      }
    } catch (err: any) {
      setError('Failed to create checkout session');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (monthly: number, annual: number) => {
    if (monthly === 0) return 'Free';
    
    if (billingCycle === 'monthly') {
      return `$${monthly}/mo`;
    } else {
      const monthlyEquivalent = annual / 12;
      return (
        <>
          <span className="text-3xl font-bold">${monthlyEquivalent.toFixed(0)}</span>
          <span className="text-lg text-gray-600">/mo</span>
          <div className="text-sm text-gray-500 mt-1">
            ${annual}/year (save ${(monthly * 12 - annual).toFixed(0)})
          </div>
        </>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Professional fire alarm design tools for every need
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-white rounded-lg p-1 shadow-sm">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
                billingCycle === 'annual'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Annual
              <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="max-w-2xl mx-auto mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`bg-white rounded-xl shadow-lg overflow-hidden transition-transform hover:scale-105 ${
                tier.popular ? 'ring-2 ring-blue-600' : ''
              }`}
            >
              {tier.popular && (
                <div className="bg-blue-600 text-white text-center py-2 text-sm font-semibold">
                  Most Popular
                </div>
              )}

              <div className="p-6">
                {/* Tier Name */}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {tier.displayName}
                </h3>
                <p className="text-gray-600 text-sm mb-6">
                  {tier.description}
                </p>

                {/* Price */}
                <div className="mb-6">
                  {typeof formatPrice(tier.priceMonthly, tier.priceAnnual) === 'string' ? (
                    <div className="text-3xl font-bold text-gray-900">
                      {formatPrice(tier.priceMonthly, tier.priceAnnual)}
                    </div>
                  ) : (
                    <div className="text-gray-900">
                      {formatPrice(tier.priceMonthly, tier.priceAnnual)}
                    </div>
                  )}
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => handleSelectPlan(tier)}
                  disabled={loading}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-colors mb-6 ${
                    tier.popular
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading ? <LoadingSpinner size="sm" /> : tier.cta}
                </button>

                {/* Features */}
                <ul className="space-y-3">
                  {tier.features.map((feature, index) => (
                    <li key={index} className="flex items-start text-sm">
                      <svg
                        className="w-5 h-5 text-green-500 mr-2 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="bg-white rounded-xl shadow-lg p-8 space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Can I change plans later?
              </h3>
              <p className="text-gray-600 text-sm">
                Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately for upgrades, or at the end of your billing period for downgrades.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                What payment methods do you accept?
              </h3>
              <p className="text-gray-600 text-sm">
                We accept all major credit cards (Visa, MasterCard, American Express) through our secure Stripe payment processor.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Is there a free trial?
              </h3>
              <p className="text-gray-600 text-sm">
                The Starter plan is free forever with no credit card required. You can upgrade to a paid plan at any time to unlock additional features.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">
                Can I cancel anytime?
              </h3>
              <p className="text-gray-600 text-sm">
                Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your billing period.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
